/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Padding } from 'c3';
import { Property } from 'csstype';
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { BasicChart } from 'src/core/components/BasicChart';
import { getScopedTheme, getStylist, joinClassNames } from 'src/core/themes/luisStylist';
import { ChartDataProps } from 'src/core/types/chart';
import { transformDonutChartData } from 'src/core/utils/chartDataTransforms';

require('./donutChartStyles.less');

type DonutSize = 'small' | 'medium' | 'large';

export type DonutChartProps = ChartDataProps<string, number> & {
    /**
     * Array of sections colors.
     */
    colors?: string[];

    /**
     * The padding spacing of the donut chart.
     */
    padding?: Padding;

    /**
     * The width of the donut.
     */
    width?: number;

    /**
     * The title to be displayed in the donut.
     */
    donutTitle?: string;

    /**
     * The subtitle to be displayed in the donut.
     */
    donutSubtitle?: string;

    /**
     * The size of the chart.
     */
    size?: DonutSize;

    /**
     *  Whether the titles should appear in order (title then subtitle) or not.
     */
    areTitlesInOrder?: boolean;

    /**
     * Title fontWeight
     */
    titleFontWeight?: string;

    /**
     * subtitle fontWeight
     */
    subtitleFontWeight?: string;
};

const donutChartTheme = getScopedTheme('DonutChart');

const { getClassName } = getStylist('DonutChart');

const chart = getClassName('Chart', {
    marginLeft: 'auto',
    marginRight: 'auto'
});

const chartSubtitleClass = (fontWeight: string) =>
    getClassName('ChartSubtitle', theme => ({
        fontSize: theme.subtitleFontSize,
        lineHeight: theme.subtitleLineHeight,
        fontWeight: fontWeight as Property.FontWeight
    }));

const chartLargeTitleClass = (fontWeight: string) =>
    getClassName('ChartLargeTitle', theme => ({
        fontSize: theme.largeTitleFontSize,
        lineHeight: theme.largeTitleLineHeight,
        letterSpacing: theme.largeTitleLetterSpacing,
        fontWeight: (fontWeight ?? theme.largeTitleFontWeight) as Property.FontWeight
    }));

const chartMediumTitleClass = (fontWeight: string) =>
    getClassName('ChartMediumTitle', theme => ({
        fontSize: theme.mediumTitleFontSize,
        lineHeight: theme.mediumTitleLineHeight,
        letterSpacing: theme.mediumTitleLetterSpacing,
        fontWeight: fontWeight as Property.FontWeight
    }));

/**
 * The donut chart component
 *
 * @params props the properties of the donut chart.
 */
export const DonutChart = (props: DonutChartProps) => {
    const {
        size,
        xData,
        yData,
        width = 60,
        padding = {},
        donutSubtitle,
        donutTitle = '',
        titleFontWeight,
        subtitleFontWeight,
        areTitlesInOrder = true,
        colors = [donutChartTheme.firstColor, donutChartTheme.secondColor, donutChartTheme.thirdColor]
    } = props;

    const jsDonutChartClassName = 'js-donut-chart';
    const [isRendered, setIsRendered] = React.useState(false);
    const basicChartRef = React.useRef<HTMLDivElement>();

    let donutSize: number;

    switch (size) {
        case 'small':
            donutSize = donutChartTheme.smallDonut;
            break;
        case 'medium':
            donutSize = donutChartTheme.mediumDonut;
            break;
        case 'large':
            donutSize = donutChartTheme.largeDonut;
            break;
        default:
    }

    const configs: c3.ChartConfiguration = {
        data: {
            columns: transformDonutChartData(xData, yData),
            type: 'donut',
            color: (_, d) => {
                const index: number = xData.data.indexOf(d as string) % colors.length;

                return colors[index < 0 ? 0 : index];
            },
            order: null
        },

        interaction: {
            enabled: false
        },

        padding,

        donut: {
            width,
            label: {
                show: false
            }
        },

        size: {
            width: donutSize,
            height: donutSize
        },

        onrendered: () => setIsRendered(true)
    };

    React.useEffect(() => {
        if (isRendered) {
            createRoot(basicChartRef.current.querySelector('.js-donut-chart .c3-chart-arcs-title')).render(
                <ChartSubtitle
                    size={size}
                    title={donutTitle}
                    subtitle={donutSubtitle}
                    titleFontWeight={titleFontWeight}
                    areTitlesInOrder={areTitlesInOrder}
                    subtitleFontWeight={subtitleFontWeight}
                />
            );
        }
    }, [donutTitle, donutSubtitle, size, isRendered]);

    return <BasicChart ref={basicChartRef} className={joinClassNames(jsDonutChartClassName, chart)} {...configs} />;
};

/**
 * Display the donut subtitle.
 *
 * @param subtitle The displayed subtitle
 */
const ChartSubtitle = (props: {
    title: string;
    subtitle: string;
    size: DonutSize;
    titleFontWeight: string;
    subtitleFontWeight: string;
    areTitlesInOrder: boolean;
}) => {
    const { title, subtitle, size, titleFontWeight, subtitleFontWeight, areTitlesInOrder } = props;

    return areTitlesInOrder ? (
        <>
            <tspan className={size === 'large' ? chartLargeTitleClass(titleFontWeight) : chartMediumTitleClass(titleFontWeight)}>
                {title}
            </tspan>
            <tspan
                y={size === 'large' ? '6mm' : '5mm'}
                x={size === 'large' ? '0.3mm' : '0.1mm'}
                className={chartSubtitleClass(subtitleFontWeight)}>
                {subtitle}
            </tspan>
        </>
    ) : (
        <>
            <tspan y={size === 'large' ? '-2mm' : '-5mm'} className={chartSubtitleClass(subtitleFontWeight)}>
                {subtitle}
            </tspan>
            <tspan
                y={size === 'large' ? '3mm' : '2mm'}
                x={size === 'large' ? '0.3mm' : '0.1mm'}
                className={size === 'large' ? chartLargeTitleClass(titleFontWeight) : chartMediumTitleClass(titleFontWeight)}>
                {title}
            </tspan>
        </>
    );
};
